import {
  AreaChart,
  Card,
  IndicatorLabel,
  IndicatorLabelColor,
  Skeleton,
  StatsCircleIcon,
  Typography,
  ZeroState,
} from "@codat/orchard-ui";
import { useGetCumulativeCreatedCompaniesQuery } from "api/portal";
import React, { useEffect, useMemo, useState } from "react";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import { objectKeys } from "utils/typescript";

import styles from "./CreatedCompaniesChart.module.scss";

interface AreaChartDataPoint extends Record<string, string | number> {
  name: string;
  value: number;
}

export const labelStyles: React.CSSProperties = {
  fill: "rgb(85, 102, 128)",
  fontFamily: "Axiforma",
  fontSize: "0.75rem",
  opacity: "0.6",
  lineHeight: "0.75",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomizedAxisTick = ({ x, y, index, payload }: any) => (
  <g transform={`translate(${x},${y})`}>
    <text x={index == 0 ? 70 : 0} y={24} textAnchor="end" style={labelStyles}>
      {payload.value}
    </text>
  </g>
);

const CreatedCompaniesChartWrapper = ({
  label,
  color,
  children,
}: {
  label: string;
  color: IndicatorLabelColor;
  children: React.ReactNode;
}) => (
  <Card
    size="small"
    data-testid="created-companies-card"
    className={styles.card}
  >
    <div className={styles.header}>
      <Typography
        as="p"
        variant="heading_sm"
        className={styles.text}
        data-testid="created-companies-card-title"
      >
        Companies
      </Typography>
      <IndicatorLabel
        label={label}
        color={color}
        className={styles.indicatorLabel}
      />
    </div>
    {children}
  </Card>
);

const CreatedCompaniesChart = () => {
  const [yAxisRange, setYAxisRange] = useState<[number, number]>();
  const [{ data, loading, error }] = useGetCumulativeCreatedCompaniesQuery({
    period: 90,
  });

  const chartData: AreaChartDataPoint[] | null = useMemo(() => {
    if (!data) return null;
    return objectKeys(data).map((x) => {
      return {
        name: formatDateAndTimeWithShortString(x),
        value: data[x],
      };
    });
  }, [data]);

  useEffect(() => {
    if (chartData) {
      setYAxisRange([0, Math.max(...chartData.map((x) => x.value)) * 1.1]);
    }
  }, [chartData]);

  if (error) {
    return (
      <CreatedCompaniesChartWrapper label="No value found" color="interface">
        <div className={styles.zeroState}>
          <ZeroState title="No data found" icon={<StatsCircleIcon />} />
        </div>
      </CreatedCompaniesChartWrapper>
    );
  }

  if (loading || !chartData) {
    return (
      <Card
        size="small"
        data-testid="created-companies-card"
        className={styles.card}
        aria-busy={true}
      >
        <Skeleton width="100%" height="100%" />
      </Card>
    );
  }

  return (
    <CreatedCompaniesChartWrapper
      label={
        chartData.length
          ? chartData[chartData.length - 1].value.toString()
          : "0"
      }
      color="action-dark"
    >
      <AreaChart
        data={chartData}
        color="#006FFB"
        xAxisProps={{
          dataKey: "name",
          tick: <CustomizedAxisTick />,
          interval: chartData.length - 2,
        }}
        tooltipProps={{
          formatter: (value) => `${value} companies`,
        }}
        yAxisProps={{
          hide: true,
          type: "number",
          domain: yAxisRange,
        }}
      />
    </CreatedCompaniesChartWrapper>
  );
};

export default CreatedCompaniesChart;
